/**
 * Adequacy-questionnaire client (#52). Preview is read-only (live
 * scoring while the adviser fills the MiFID tab); create files the
 * assessment via maker-checker; list returns the agreement's history.
 */
import { api } from './client';
import type {
  AqPreview, AqResponse, AqSubmission, Page, StrategicAllocation,
  SuitabilityConversionRow, UUID,
} from './types';

export const aqApi = {
  preview: (agreementId: UUID, body: AqSubmission): Promise<AqPreview> =>
    api.post(`/agreements/${agreementId}/aq-preview`, body),
  create: (agreementId: UUID, body: AqSubmission): Promise<AqResponse> =>
    api.post(`/agreements/${agreementId}/aq-responses`, body),
  list: (agreementId: UUID): Promise<Page<AqResponse>> =>
    // 200 = the server maximum; an agreement's history is small by nature (newest first).
    api.get(`/agreements/${agreementId}/aq-responses?limit=200`),
  /** #52 polish: per-version soft-delete (staged on the page; Save commits). */
  delete: (agreementId: UUID, responseId: UUID): Promise<AqResponse> =>
    api.delete(`/agreements/${agreementId}/aq-responses/${responseId}`),
};

/** Allocation ordering for the client-side exceeds flash (mirror of
 *  app/services/suitability.py — the saved flag is server-computed). */
export const ALLOCATION_ORDER: StrategicAllocation[] = [
  'CASH', 'CASH_PLUS', 'STB',
  'SAA00', 'SAA10', 'SAA20', 'SAA30', 'SAA40', 'SAA50',
  'SAA60', 'SAA70', 'SAA80', 'SAA90', 'SAA100',
];

export function matchConversionRow(
  rows: SuitabilityConversionRow[], score: number | null,
): SuitabilityConversionRow | null {
  if (score === null) return null;
  return rows.find(r =>
    (score > r.score_from && score <= r.score_to)
    || (score === r.score_from && r.score_from === -10),
  ) ?? null;
}

export function allocationExceeds(
  rows: SuitabilityConversionRow[],
  score: number | null,
  allocation: string | null | undefined,
): boolean | null {
  if (score === null || !allocation) return null;
  const row = matchConversionRow(rows, score);
  if (!row || row.allocations.length === 0) return null;
  const rank = ALLOCATION_ORDER.indexOf(allocation as StrategicAllocation);
  if (rank < 0) return null;
  const ceiling = Math.max(...row.allocations.map(a => ALLOCATION_ORDER.indexOf(a)));
  return rank > ceiling;
}

export const suitabilityConversionApi = {
  list: (): Promise<Page<SuitabilityConversionRow>> =>
    api.get('/suitability-conversion?limit=200'),
};
